import { useEffect, useRef } from 'react';

/**
 * useScrollReveal
 * Returns a `ref` callback to attach to any element.
 * When that element scrolls into view, `.sr-visible` is added (once).
 * Pair with `.sr-item` CSS class for the fade-up entrance.
 *
 * Usage:
 *   const reveal = useScrollReveal();
 *   <div className="sr-item" ref={reveal}> ... </div>
 *
 * For stagger groups, wrap children in `.sr-stagger` and attach reveal
 * to the wrapper — all .sr-item children animate in sequence via CSS delays.
 */
export function useScrollReveal({ threshold = 0.12, rootMargin = '0px 0px -60px 0px' } = {}) {
  const elements = useRef([]);
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('sr-visible');
            entry.target.querySelectorAll('.sr-item').forEach((child) => {
              child.classList.add('sr-visible');
            });
            observerRef.current.unobserve(entry.target);
          }
        });
      },
      { threshold, rootMargin }
    );

    elements.current.forEach((el) => { if (el) observerRef.current.observe(el); });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [threshold, rootMargin]);

  const ref = (el) => {
    if (el && !elements.current.includes(el)) {
      elements.current.push(el);
      if (observerRef.current) {
        observerRef.current.observe(el);
      }
    }
  };

  return ref;
}
